import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button } from '../../Components/Button/Button';
import uuidv4 from '../../utils/uuidv4';
/**
 * Bronson Toggle object.
 * The `toggle` pattern is an accessible content-reveal object.
 * It can show/hide (or expand/collapse) a {@link targetRef} element.
 * It can be used standalone or placed within other components to reveal/hide
 * a target element. Each Toggle object has a 1-to-1 relationship to its target,
 * but a target can be controlled via multiple `toggle` patterns.
 * @see https://bronson.vwfs.io/default/docs/objects/toggle.html
 * @constructor
 */
export function Toggle({ className, defaultHidden = true, hidden, id, labelHidden, labelUnhidden, onClick, targetId, targetRef, testId, ...otherProps }) {
    const classNameList = classNames('o-toggle', className).trim();
    const [isHidden, setIsHidden] = useState(defaultHidden);
    const [toggleId] = useState(id ?? uuidv4());
    /**
     * Handle side effects for the {@link targetRef}.
     */
    useEffect(() => {
        if (targetRef?.current) {
            targetRef?.current.setAttribute('aria-hidden', `${hidden ?? isHidden}`);
            if (hidden ?? isHidden) {
                targetRef?.current.setAttribute('inert', '');
            }
            else {
                targetRef?.current.removeAttribute('inert');
            }
        }
        /**
         * Cleanup function.
         */
        return () => { };
    }, [isHidden, targetRef, hidden]);
    useEffect(() => {
        if (targetRef?.current) {
            const ariaLabelledBy = targetRef?.current.getAttribute('aria-labelledby');
            const ariaLabelledByValue = ariaLabelledBy !== toggleId && ariaLabelledBy ? `${ariaLabelledBy} ${toggleId}` : toggleId;
            targetRef?.current.setAttribute('aria-labelledby', ariaLabelledByValue);
        }
        /**
         * Cleanup function.
         */
        return () => { };
    }, [targetRef, toggleId]);
    /**
     * Handle click events on the Toggle button.
     * @type {(function(*): void)|*}
     */
    const handleClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        /**
         * Only change internal state when in uncontrolled mode.
         */
        if (!hidden) {
            setIsHidden(!isHidden);
        }
        /**
         * Call custom callback.
         */
        onClick?.(hidden ?? isHidden);
    }, [isHidden, onClick]);
    return (React.createElement(Button, { className: classNameList, id: toggleId, onClick: handleClick, ariaExpanded: `${!(hidden ?? isHidden)}`, "aria-controls": targetId, testId: testId, ...otherProps },
        React.createElement("span", { className: "o-toggle__label o-toggle__label--target-hidden" }, labelHidden),
        React.createElement("span", { className: "o-toggle__label o-toggle__label--target-unhidden" }, labelUnhidden)));
}
